import { CommonModule } from '@angular/common';

import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit
} from '@angular/core';

import { FormsModule } from '@angular/forms';

import {
  DeleteRequest,
  StaffMember
} from '../../../../models/app.models';

import { StaffMemberService } from '../../../../services/staff-member.service';
import { MediaService } from '../../../../services/media.service';
import { ToastService } from '../../../../services/toast.service';

@Component({
  selector: 'app-admin-staff',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule
  ],
  templateUrl: './admin-staff.component.html',
  styleUrls: [
    './admin-staff.component.css',
    '../../admin-section.shared.css'
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AdminStaffComponent
  implements OnInit {

  readonly requiredFieldsMessage =
    'Complete all required fields.';

  staffMembers: StaffMember[] = [];

  staffMembersLoading = true;

  selectedStaffImage: File | null = null;

  staffEditorOpen = false;

  staffDraft: StaffMember =
    this.emptyStaffMember();

  deleteRequest: DeleteRequest | null = null;

  constructor(
    private readonly staffMemberService:
      StaffMemberService,
    private readonly mediaService:
      MediaService,
    private readonly toastService:
      ToastService,
    private readonly changeDetector:
      ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.loadStaffMembers();
  }

  loadStaffMembers(): void {
    this.staffMembersLoading = true;

    this.staffMemberService
      .getAdminStaffMembers()
      .subscribe({
        next: members => {
          this.staffMembers = members;

          this.staffMembersLoading = false;

          this.changeDetector.markForCheck();
        },

        error: error => {
          console.error(
            'Unable to load staff members:',
            error
          );

          this.staffMembersLoading = false;

          this.toastService.error(
            'Unable to load staff members.'
          );

          this.changeDetector.markForCheck();
        }
      });
  }

  openStaffEditor(
    member?: StaffMember
  ): void {
    this.staffDraft = member
      ? { ...member }
      : this.emptyStaffMember();

    this.selectedStaffImage = null;

    this.staffEditorOpen = true;
  }

  closeStaffEditor(): void {
    this.selectedStaffImage = null;

    this.staffDraft =
      this.emptyStaffMember();

    this.staffEditorOpen = false;
  }

  saveStaffMember(): void {
    if (
      !this.staffDraft.name.trim() ||
      !this.staffDraft.title.trim()
    ) {
      this.toastService.error(
        this.requiredFieldsMessage
      );

      return;
    }

    if (this.selectedStaffImage) {
      this.mediaService
        .uploadImage(
          this.selectedStaffImage
        )
        .subscribe({
          next: media => {
            this.staffDraft.imageId =
              media.id;

            this.persistStaff();
          },

          error: error => {
            console.error(
              'Unable to upload staff image:',
              error
            );

            this.toastService.error(
              'Unable to upload image.'
            );

            this.changeDetector.markForCheck();
          }
        });

      return;
    }

    this.persistStaff();
  }

  onImageSelected(
    event: Event,
    _target: 'post' | 'staff' = 'staff'
  ): void {
    const input =
      event.target as HTMLInputElement;

    const file =
      input.files?.[0];

    if (!file) {
      return;
    }

    if (!file.type.startsWith('image/')) {
      this.toastService.error(
        'Please select a valid image.'
      );

      input.value = '';

      return;
    }

    this.selectedStaffImage = file;

    const reader =
      new FileReader();

    reader.onload = () => {
      this.staffDraft.imageUrl =
        String(reader.result ?? '');

      this.changeDetector.markForCheck();
    };

    reader.onerror = () => {
      this.toastService.error(
        'Unable to process the selected image.'
      );

      input.value = '';
    };

    reader.readAsDataURL(file);
  }

  removeDraftImage(
    _target: 'post' | 'staff' = 'staff'
  ): void {
    this.staffDraft.imageUrl = '';
    this.staffDraft.imageId = null;

    this.selectedStaffImage = null;

    this.changeDetector.markForCheck();
  }

  requestDelete(
    kind: DeleteRequest['kind'],
    id: number,
    label: string
  ): void {
    this.deleteRequest = {
      kind,
      id,
      label
    };
  }

  confirmDelete(): void {
    if (!this.deleteRequest) {
      return;
    }

    this.deleteStaffMember(
      this.deleteRequest.id
    );
  }

  cancelDelete(): void {
    this.deleteRequest = null;
  }

  trackById(
    _: number,
    item: { id: number }
  ): number {
    return item.id;
  }

  private persistStaff(): void {
    if (this.staffDraft.id) {
      this.staffMemberService
        .updateStaffMember(
          this.staffDraft
        )
        .subscribe({
          next: () => {
            this.selectedStaffImage = null;

            this.staffDraft =
              this.emptyStaffMember();

            this.staffEditorOpen = false;

            this.toastService.success(
              'Staff member saved.'
            );

            this.loadStaffMembers();

            this.changeDetector.markForCheck();
          },

          error: error => {
            console.error(
              'Unable to update staff member:',
              error
            );

            this.toastService.error(
              'Unable to save staff member.'
            );

            this.changeDetector.markForCheck();
          }
        });

      return;
    }

    this.staffMemberService
      .createStaffMember(
        this.staffDraft
      )
      .subscribe({
        next: savedMember => {
          this.staffMembers = [
            ...this.staffMembers,
            savedMember
          ].sort(
            (a, b) =>
              a.displayOrder -
              b.displayOrder
          );

          this.selectedStaffImage = null;

          this.staffDraft =
            this.emptyStaffMember();

          this.staffEditorOpen = false;

          this.toastService.success(
            'Staff member saved.'
          );

          this.changeDetector.markForCheck();
        },

        error: error => {
          console.error(
            'Unable to create staff member:',
            error
          );

          this.toastService.error(
            'Unable to save staff member.'
          );

          this.changeDetector.markForCheck();
        }
      });
  }

  private deleteStaffMember(
    id: number
  ): void {
    this.staffMemberService
      .deleteStaffMember(id)
      .subscribe({
        next: () => {
          this.staffMembers =
            this.staffMembers.filter(
              member =>
                member.id !== id
            );

          this.deleteRequest = null;

          this.toastService.success(
            'Staff member deleted.'
          );

          this.changeDetector.markForCheck();
        },

        error: error => {
          console.error(
            'Unable to delete staff member:',
            error
          );

          this.toastService.error(
            'Unable to delete staff member.'
          );

          this.changeDetector.markForCheck();
        }
      });
  }

  private emptyStaffMember():
    StaffMember {
    return {
      id: 0,
      name: '',
      title: '',
      bio: '',
      imageId: null,
      imageUrl: '',
      displayOrder: 0,
      isActive: true
    };
  }
}